/**
 * @fileoverview Maintenance Activity Page - Step 2 Workflow
 * @module MaintenanceActivityPage
 * 
 * Handles the complete Step 2 workflow:
 * - Creating/managing maintenance reports
 * - Navigating between category checklists
 * - Photo upload integration
 * - Summary view
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import {
  CategoryCard,
  RoofChecklist,
  FoundationChecklist,
  HVACChecklist,
  PlumbingChecklist,
  ElectricalChecklist,
  MaintenanceSummary
} from '../components/maintenance';
import { PhotoUploader, EvidenceGallery } from '../components/evidence';
import {
  ArrowLeft,
  Home,
  Layers,
  Wind,
  Droplets,
  Zap,
  ClipboardCheck,
  Camera,
  BarChart3,
  Loader2
} from 'lucide-react';

type ViewMode = 'categories' | 'checklist' | 'summary';
type Category = 'ROOF' | 'FOUNDATION' | 'HVAC' | 'PLUMBING' | 'ELECTRICAL';

const categoryConfig: Record<Category, { icon: React.ReactNode; description: string }> = {
  ROOF: { icon: <Home className="w-6 h-6" />, description: 'Shingles, leaks, age, and structural issues' },
  FOUNDATION: { icon: <Layers className="w-6 h-6" />, description: 'Cracks, settling, moisture, and stability' },
  HVAC: { icon: <Wind className="w-6 h-6" />, description: 'Heating, cooling, ductwork, and efficiency' },
  PLUMBING: { icon: <Droplets className="w-6 h-6" />, description: 'Pipes, fixtures, water heater, and leaks' },
  ELECTRICAL: { icon: <Zap className="w-6 h-6" />, description: 'Panel, wiring, outlets, and safety issues' },
};

const MaintenanceActivityPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState<any>(null);
  const [report, setReport] = useState<any>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('categories');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [summary, setSummary] = useState<any>(null);

  // Fetch property and report data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get user's property
        const profileRes = await apiClient.users.getProfile();
        const profile = profileRes.data.data.profile || profileRes.data.data;
        if (profileRes.data.success && profile.properties?.length > 0) {
          const userProperty = profile.properties[0];
          setProperty(userProperty);

          // Get or create maintenance report
          let reportData;
          try {
            const reportRes = await apiClient.maintenance.getReportByProperty(userProperty.id);
            if (reportRes.data.success) {
              reportData = reportRes.data.data;
            }
          } catch {
            // Create new report
            const createRes = await apiClient.maintenance.createReport(userProperty.id);
            if (createRes.data.success) {
              reportData = createRes.data.data;
            }
          }
          
          if (reportData) {
            setReport(reportData);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  // Fetch summary when entering summary view
  useEffect(() => {
    const fetchSummary = async () => {
      if (viewMode === 'summary' && property) {
        try {
          const res = await apiClient.maintenance.getSummary(property.id);
          if (res.data.success) {
            setSummary(res.data.data);
          }
        } catch (error) {
          console.error('Error fetching summary:', error);
        }
      }
    };
    fetchSummary();
  }, [viewMode, property]);

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category);
    setViewMode('checklist');
  };

  const handleSaveFinding = async (data: any) => {
    let currentReport = report;
    
    // If report doesn't exist, try to create one
    if (!currentReport) {
      if (!property) {
        console.error('Cannot save finding: No property available');
        toast.error('Unable to save — no property found on your account. Please refresh the page and try again.');
        return;
      }
      
      try {
        // Try to get existing report first
        const reportRes = await apiClient.maintenance.getReportByProperty(property.id);
        if (reportRes.data.success) {
          currentReport = reportRes.data.data;
          setReport(currentReport);
        }
      } catch {
        // Create new report if none exists
        try {
          const createRes = await apiClient.maintenance.createReport(property.id);
          if (createRes.data.success) {
            currentReport = createRes.data.data;
            setReport(currentReport);
          }
        } catch (createErr) {
          console.error('Failed to create maintenance report:', createErr);
          toast.error('Could not create a maintenance report for your property. Please check your connection and try again.');
          return;
        }
      }
      
      if (!currentReport) {
        console.error('Failed to get or create maintenance report');
        toast.error('Unable to initialize your maintenance report. Please refresh the page and try again.');
        return;
      }
    }
    
    try {
      const res = await apiClient.maintenance.saveFinding({
        reportId: currentReport.id,
        ...data
      });
      
      if (res.data.success) {
        // Update local report with new finding (create new array to ensure React detects change)
        setReport((prev: any) => {
          const prevFindings = prev?.findings || [];
          const existingIndex = prevFindings.findIndex((f: any) => f.category === data.category);
          
          // Create a new array to avoid mutation issues
          const newFindings = [...prevFindings];
          if (existingIndex >= 0) {
            newFindings[existingIndex] = res.data.data;
          } else {
            newFindings.push(res.data.data);
          }
          
          return { ...(prev || currentReport), findings: newFindings };
        });
        
        toast.success('Assessment saved!');
        
        // Go back to categories
        setViewMode('categories');
        setSelectedCategory(null);
      }
    } catch (error) {
      console.error('Error saving finding:', error);
      // Extract backend validation message if available
      const backendMessage = (error as any)?.response?.data?.error?.message;
      if (backendMessage) {
        toast.error(`Could not save: ${backendMessage}`);
      } else {
        toast.error('Something went wrong while saving your assessment. Please check your connection and try again.');
      }
    }
  };

  const getFindingForCategory = (category: Category) => {
    return report?.findings?.find((f: any) => f.category === category);
  };

  const getCategoryIssueCount = (category: Category): number => {
    const finding = getFindingForCategory(category);
    if (!finding?.checklistData) return 0;
    return Object.values(finding.checklistData).filter(v => v === true).length;
  };

  const isCategoryComplete = (category: Category): boolean => {
    return !!getFindingForCategory(category);
  };

  const handleGenerateArguments = () => {
    navigate('/arguments');
  };

  const handleEvidenceUpload = (evidence: any) => {
    // Refresh report to include new evidence
    if (property) {
      apiClient.maintenance.getReportByProperty(property.id)
        .then(res => {
          if (res.data.success) {
            setReport(res.data.data);
          }
        });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <ClipboardCheck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">No Property Found</h2>
          <p className="text-gray-600 mb-4">Please add a property to your account first.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-slate-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-slate-800 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                if (viewMode === 'checklist') {
                  setViewMode('categories');
                  setSelectedCategory(null);
                } else {
                  navigate('/dashboard');
                }
              }}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">
                {viewMode === 'checklist' ? 'Categories' : 'Dashboard'}
              </span>
            </button>

            {/* View Toggles */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => { setViewMode('categories'); setSelectedCategory(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'categories' ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                }`}
              >
                <ClipboardCheck className="w-4 h-4" />
                Checklists
              </button>
              <button
                onClick={() => setViewMode('summary')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'summary' ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                Summary
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Property Info */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Document Maintenance Issues</h1>
          <p className="text-gray-600">
            {property.addressLine1}, {property.city}, FL {property.zipCode}
          </p>
        </div>

        {/* Categories View */}
        {viewMode === 'categories' && (
          <div className="space-y-4">
            <p className="text-gray-600 mb-6">
              Complete each checklist to document issues that may affect your property's value.
            </p>
            
            {(Object.keys(categoryConfig) as Category[]).map(category => (
              <CategoryCard
                key={category}
                title={category.charAt(0) + category.slice(1).toLowerCase()}
                description={categoryConfig[category].description}
                icon={categoryConfig[category].icon}
                issueCount={getCategoryIssueCount(category)}
                isComplete={isCategoryComplete(category)}
                onClick={() => handleCategorySelect(category)}
              />
            ))}
          </div>
        )}

        {/* Checklist View */}
        {viewMode === 'checklist' && selectedCategory && (
          <div className="space-y-8">
            {selectedCategory === 'ROOF' && (
              <RoofChecklist
                initialData={getFindingForCategory('ROOF')}
                onSave={handleSaveFinding}
                onBack={() => { setViewMode('categories'); setSelectedCategory(null); }}
                findingId={getFindingForCategory('ROOF')?.id}
                evidence={getFindingForCategory('ROOF')?.evidence || []}
                invoices={getFindingForCategory('ROOF')?.invoices || []}
                onEvidenceChange={handleEvidenceUpload}
              />
            )}
            {selectedCategory === 'FOUNDATION' && (
              <FoundationChecklist
                initialData={getFindingForCategory('FOUNDATION')}
                onSave={handleSaveFinding}
                onBack={() => { setViewMode('categories'); setSelectedCategory(null); }}
                findingId={getFindingForCategory('FOUNDATION')?.id}
                evidence={getFindingForCategory('FOUNDATION')?.evidence || []}
                invoices={getFindingForCategory('FOUNDATION')?.invoices || []}
                onEvidenceChange={handleEvidenceUpload}
              />
            )}
            {selectedCategory === 'HVAC' && (
              <HVACChecklist
                initialData={getFindingForCategory('HVAC')}
                onSave={handleSaveFinding}
                onBack={() => { setViewMode('categories'); setSelectedCategory(null); }}
                findingId={getFindingForCategory('HVAC')?.id}
                evidence={getFindingForCategory('HVAC')?.evidence || []}
                invoices={getFindingForCategory('HVAC')?.invoices || []}
                onEvidenceChange={handleEvidenceUpload}
              />
            )}
            {selectedCategory === 'PLUMBING' && (
              <PlumbingChecklist
                initialData={getFindingForCategory('PLUMBING')}
                onSave={handleSaveFinding}
                onBack={() => { setViewMode('categories'); setSelectedCategory(null); }}
                findingId={getFindingForCategory('PLUMBING')?.id}
                evidence={getFindingForCategory('PLUMBING')?.evidence || []}
                invoices={getFindingForCategory('PLUMBING')?.invoices || []}
                onEvidenceChange={handleEvidenceUpload}
              />
            )}
            {selectedCategory === 'ELECTRICAL' && (
              <ElectricalChecklist
                initialData={getFindingForCategory('ELECTRICAL')}
                onSave={handleSaveFinding}
                onBack={() => { setViewMode('categories'); setSelectedCategory(null); }}
                findingId={getFindingForCategory('ELECTRICAL')?.id}
                evidence={getFindingForCategory('ELECTRICAL')?.evidence || []}
                invoices={getFindingForCategory('ELECTRICAL')?.invoices || []}
                onEvidenceChange={handleEvidenceUpload}
              />
            )}

            {/* Photo Upload Section */}
            {getFindingForCategory(selectedCategory) && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Camera className="w-5 h-5 text-gray-600" />
                  <h3 className="font-bold text-gray-900">Add Photo Evidence</h3>
                </div>
                <p className="text-gray-600 mb-4 text-sm">
                  Upload photos of the issues you've documented to strengthen your case.
                </p>
                <PhotoUploader
                  findingId={getFindingForCategory(selectedCategory).id}
                  onUploadComplete={handleEvidenceUpload}
                />
                
                {getFindingForCategory(selectedCategory)?.evidence?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-medium text-gray-900 mb-3">Uploaded Evidence</h4>
                    <EvidenceGallery
                      evidence={getFindingForCategory(selectedCategory).evidence}
                      onDelete={() => {
                        // Refresh report
                        if (property) {
                          apiClient.maintenance.getReportByProperty(property.id)
                            .then(res => {
                              if (res.data.success) {
                                setReport(res.data.data);
                              }
                            });
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Summary View */}
        {viewMode === 'summary' && summary && (
          <MaintenanceSummary
            totalIssues={summary.totalIssues}
            totalEstimatedCost={summary.totalEstimatedCost}
            categorySummaries={summary.categorySummaries}
            overallSeverity={summary.overallSeverity}
            onGenerateArguments={handleGenerateArguments}
          />
        )}
      </div>
    </div>
  );
};

export default MaintenanceActivityPage;

